import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  BanknotesIcon,
  Bars3Icon,
  BoltIcon,
  CreditCardIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ChartPieIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  HomeIcon,
  LinkIcon,
  Squares2X2Icon,
  ShieldCheckIcon,
  SparklesIcon,
  TableCellsIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { clearSession, getEnabledModules, getSession, hasCapability, hasModulo, switchEmpresa } from '../auth/session';

const MODULE_ICON_MAP = {
  financeiro: CurrencyDollarIcon,
  construtor: BuildingOfficeIcon,
  solar: BoltIcon,
  radar_lucro: ChartBarIcon,
  personal_finance: CreditCardIcon,
};

const FALLBACK_MODULE_ICON = Squares2X2Icon;

const detectActiveContext = (pathname, enabledModules = []) => {
  if (pathname.startsWith('/clientes') || pathname.startsWith('/cobrancas')) return 'billing_core';
  if (pathname.startsWith('/cadastros/configuracao-operacional')) return 'construtor';
  if (pathname.startsWith('/cadastros')) return 'master_data';
  if (pathname.startsWith('/pessoal')) return 'personal_finance';
  if (pathname.startsWith('/solar')) return 'solar';
  if (pathname.startsWith('/radar-lucro')) return 'radar_lucro';
  if (pathname.startsWith('/construtor')) return 'construtor';
  if (pathname.startsWith('/admin')) return 'gestao';
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/lancamentos') || pathname.startsWith('/conciliacao-bancaria') || pathname.startsWith('/relatorios')) return 'financeiro';

  const dynamicModule = enabledModules.find((item) => {
    const route = String(item?.default_route || '').trim();
    return route && route !== '/' && pathname.startsWith(route);
  });

  return dynamicModule?.key || (enabledModules[0]?.key || 'financeiro');
};

const Sidebar = ({
  contextSidebarPinned = true,
  onToggleContextSidebar = () => {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const contextSidebarCloseTimerRef = useRef(null);

  const [financeiroRelatoriosOpen, setFinanceiroRelatoriosOpen] = useState(location.pathname.startsWith('/relatorios'));
  const [contextSidebarHoverOpen, setContextSidebarHoverOpen] = useState(false);

  const session = getSession();
  const empresas = session?.empresas || [];
  const empresaId = session?.empresaId || '';

  const empresaNome = useMemo(() => {
    const current = empresas.find((e) => e.empresa_id === empresaId);
    return current?.nome || 'Empresa';
  }, [empresas, empresaId]);

  const canManageTenant = ['owner', 'admin'].includes(session?.user?.role_global);
  const enabledSoldModules = getEnabledModules();
  const moduloConstrutorAtivo = hasModulo('construtor');
  const canAccessBilling = hasCapability('billing_core');
  const canAccessCollections = hasCapability('collections');
  const canAccessCadastros = hasCapability('master_data');

  const activeModule = detectActiveContext(location.pathname, enabledSoldModules);

  const moduleButtons = useMemo(
    () => [
      ...(canAccessBilling
        ? [
            {
              id: 'billing_core',
              label: 'Cobrancas',
              icon: CurrencyDollarIcon,
              enabled: true,
              href: '/cobrancas',
            },
          ]
        : []),
      ...enabledSoldModules.map((moduleItem) => ({
        id: moduleItem.key,
        label: moduleItem.nome,
        icon: MODULE_ICON_MAP[moduleItem.key] || FALLBACK_MODULE_ICON,
        enabled: true,
        href: moduleItem.default_route || '/',
      })),
      ...(canAccessCadastros
        ? [
            {
              id: 'master_data',
              label: 'Cadastros',
              icon: Cog6ToothIcon,
              enabled: true,
              href: '/cadastros/empresa',
            },
          ]
        : []),
      ...(canManageTenant
        ? [
            {
              id: 'gestao',
              label: 'Gestao',
              icon: ShieldCheckIcon,
              enabled: true,
              href: '/admin/gestao',
            },
          ]
        : []),
    ],
    [canAccessBilling, canAccessCadastros, canManageTenant, enabledSoldModules]
  );

  const enabledModules = moduleButtons.filter((item) => item.enabled);

  useEffect(() => {
    if (contextSidebarPinned) {
      setContextSidebarHoverOpen(false);
    }
  }, [contextSidebarPinned]);

  useEffect(
    () => () => {
      if (contextSidebarCloseTimerRef.current) {
        clearTimeout(contextSidebarCloseTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (location.pathname === '/') return;
    if (enabledModules.length === 0) return;
    const activeEnabled = enabledModules.some((item) => item.id === activeModule);
    if (!activeEnabled) {
      navigate(enabledModules[0].href, { replace: true });
    }
  }, [activeModule, enabledModules, location.pathname, navigate]);

  const financeiroItems = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Lancamentos', href: '/lancamentos', icon: BanknotesIcon },
    { name: 'Conciliacao Bancaria', href: '/conciliacao-bancaria', icon: LinkIcon },
  ];

  const coreItems = [
    { name: 'Clientes', href: '/clientes', icon: UserGroupIcon },
    { name: 'Cobrancas', href: '/cobrancas', icon: CurrencyDollarIcon },
    ...(canAccessCollections
      ? [
          { name: 'Pipeline Recuperacao', href: '/cobrancas/recuperacao', icon: SparklesIcon },
          { name: 'Reguas', href: '/cobrancas/recuperacao/estrategias', icon: Cog6ToothIcon },
          { name: 'Templates', href: '/cobrancas/recuperacao/templates', icon: DocumentTextIcon },
          { name: 'Canais', href: '/cobrancas/recuperacao/canais', icon: BoltIcon },
          { name: 'Acordos', href: '/cobrancas/recuperacao/acordos', icon: ClipboardDocumentListIcon },
          { name: 'Metricas', href: '/cobrancas/recuperacao/metricas', icon: ChartBarIcon },
        ]
      : []),
  ];

  const solarSections = [
    [{ name: 'Dashboard', href: '/solar/dashboard', icon: HomeIcon }],
    [
      { name: 'Usinas', href: '/solar/usinas', icon: BuildingOffice2Icon },
      { name: 'UCs', href: '/solar/unidades', icon: BuildingOfficeIcon },
      { name: 'Contratos', href: '/solar/contratos', icon: DocumentTextIcon },
    ],
    [
      { name: 'Faturas ENEL', href: '/solar/faturas-enel', icon: ClipboardDocumentListIcon },
      { name: 'Documentos', href: '/solar/documentos', icon: TableCellsIcon },
    ],
    [{ name: 'Configuracoes', href: '/solar/configuracoes', icon: Cog6ToothIcon }],
  ];

  const personalFinanceItems = [
    { name: 'Inicio', href: '/pessoal/inicio', icon: HomeIcon },
    { name: 'Caixa', href: '/pessoal/caixa', icon: ChartBarIcon },
    { name: 'Insights', href: '/pessoal/insights', icon: SparklesIcon },
    { name: 'Patrimonio', href: '/pessoal/patrimonio', icon: ChartPieIcon },
    { name: 'Contas', href: '/pessoal/contas', icon: BanknotesIcon },
    { name: 'Cartoes', href: '/pessoal/cartoes', icon: CreditCardIcon },
    { name: 'Metas', href: '/pessoal/metas', icon: SparklesIcon },
    { name: 'Revisao', href: '/pessoal/revisao-semanal', icon: ClipboardDocumentListIcon },
    { name: 'Conexoes', href: '/pessoal/conexoes', icon: BoltIcon },
    { name: 'Ajustes', href: '/pessoal/ajustes', icon: Cog6ToothIcon },
  ];

  const cadastrosBaseItems = [
    { name: 'Empresa', href: '/cadastros/empresa', icon: BuildingOfficeIcon },
    { name: 'Contas Bancarias', href: '/cadastros/contas-bancarias', icon: BanknotesIcon },
    { name: 'Plano de Contas', href: '/cadastros/plano-contas', icon: DocumentTextIcon },
    { name: 'Centro de Custos', href: '/cadastros/centro-custos', icon: ChartPieIcon },
    { name: 'Estrutura DRE', href: '/cadastros/dre-estrutura', icon: ChartBarIcon },
    { name: 'Mapeamentos Extrato', href: '/cadastros/mapeamentos-extrato', icon: TableCellsIcon },
  ];
  const cadastrosConfigItems = [
    ...(canAccessCadastros
      ? [
          { name: 'Contas Contabeis (Construtor)', href: '/cadastros/construtor-contas-contabeis', icon: DocumentTextIcon },
          { name: 'Centros de Custo (Construtor)', href: '/cadastros/construtor-centros-custo', icon: ChartPieIcon },
        ]
      : []),
    ...(moduloConstrutorAtivo ? [{ name: 'Configuracao Operacional', href: '/cadastros/configuracao-operacional', icon: Cog6ToothIcon }] : []),
  ];

  const relatoriosItems = [
    { name: 'Central de Relatorios', href: '/relatorios', icon: TableCellsIcon },
    { name: 'DRE Visao Geral', href: '/relatorios/dre-visao-geral', icon: TableCellsIcon },
    { name: 'DRE Comparativa', href: '/relatorios/dre-comparativa', icon: ChartBarIcon },
    { name: 'Orcado vs Realizado', href: '/relatorios/orcado-realizado', icon: BanknotesIcon },
    { name: 'Fluxo Caixa 90d', href: '/relatorios/fluxo-caixa-projetado', icon: ChartBarIcon },
    { name: 'Fechamento Mensal', href: '/relatorios/fechamento-mensal', icon: DocumentTextIcon },
    { name: 'Aging Pagar/Receber', href: '/relatorios/aging-pagar-receber', icon: TableCellsIcon },
    { name: 'Qualidade Dados', href: '/relatorios/qualidade-dados', icon: Cog6ToothIcon },
    { name: 'Margem Centro/Plano', href: '/relatorios/margem-centro-plano', icon: ChartPieIcon },
    { name: 'Movimentacao Patrimonial', href: '/relatorios/movimentacao-patrimonial', icon: ChartPieIcon },
    { name: 'Forecast Rolling', href: '/relatorios/forecast-rolling', icon: ChartBarIcon },
    { name: 'Painel Executivo', href: '/relatorios/painel-executivo', icon: ChartBarIcon },
    { name: 'Dashboard Avancado', href: '/relatorios/dashboard-avancado', icon: ChartPieIcon },
    { name: 'DRE Sintetico', href: '/relatorios/dre', icon: ChartBarIcon },
    { name: 'DRE Analitico', href: '/relatorios/dre-analitico', icon: TableCellsIcon },
  ];

  const radarItems = [
    { name: 'Radar de Lucro', href: '/radar-lucro', icon: ChartBarIcon },
    { name: 'Ruptura', href: '/radar-lucro/ruptura', icon: TableCellsIcon },
    { name: 'Capital Parado', href: '/radar-lucro/capital-parado', icon: BanknotesIcon },
    { name: 'Margem em Perigo', href: '/radar-lucro/margem', icon: ChartPieIcon },
    { name: 'Saude Estoque', href: '/radar-lucro/saude-estoque', icon: Cog6ToothIcon },
    { name: 'Acoes Hoje', href: '/radar-lucro/acoes', icon: DocumentTextIcon },
    { name: 'Queda de Giro', href: '/radar-lucro/queda-giro', icon: BanknotesIcon },
    { name: 'Comparativo Radar', href: '/radar-lucro/comparativo', icon: ChartBarIcon },
    { name: 'Evolucao Radar', href: '/radar-lucro/evolucao', icon: ChartBarIcon },
    { name: 'Valor Capturado', href: '/radar-lucro/valor-capturado', icon: CurrencyDollarIcon },
  ];

  const construtorItems = [
    { name: 'Visao Geral', href: '/construtor', icon: HomeIcon },
    { name: 'Obras', href: '/construtor/obras', icon: BuildingOffice2Icon },
    { name: 'Orcamentos', href: '/construtor/orcamentos', icon: TableCellsIcon },
    { name: 'Lancamentos', href: '/construtor/lancamentos', icon: BanknotesIcon },
    { name: 'Vendas', href: '/construtor/vendas', icon: CurrencyDollarIcon },
    { name: 'Indicadores', href: '/construtor/indicadores', icon: ChartBarIcon },
    { name: 'Contratos e Medicoes', href: '/construtor/contratos', icon: ClipboardDocumentListIcon },
    { name: 'Pessoas', href: '/construtor/pessoas', icon: UserGroupIcon },
  ];

  const gestaoItems = canManageTenant
    ? [
        { name: 'Gestao SaaS', href: '/admin/gestao', icon: ShieldCheckIcon },
        { name: 'Integracoes Cron', href: '/admin/integracoes', icon: TableCellsIcon },
        { name: 'Plano e Pagamento', href: '/admin/plano-pagamento', icon: BanknotesIcon },
      ]
    : [];

  const configuracoesItems = [
    ...(canAccessCadastros ? [{ name: 'Empresa', href: '/cadastros/empresa', icon: BuildingOfficeIcon }] : []),
    ...(canManageTenant ? [{ name: 'Gestao SaaS', href: '/admin/gestao', icon: ShieldCheckIcon }] : []),
  ];

  const handleLogout = () => {
    clearSession();
    navigate('/login', { replace: true });
  };

  const handleEmpresaChange = (newEmpresaId) => {
    switchEmpresa(newEmpresaId);
    window.location.href = '/';
  };

  const openContextSidebarPreview = () => {
    if (contextSidebarCloseTimerRef.current) {
      clearTimeout(contextSidebarCloseTimerRef.current);
      contextSidebarCloseTimerRef.current = null;
    }
    if (!contextSidebarPinned) {
      setContextSidebarHoverOpen(true);
    }
  };

  const closeContextSidebarPreview = () => {
    if (contextSidebarPinned) return;
    if (contextSidebarCloseTimerRef.current) {
      clearTimeout(contextSidebarCloseTimerRef.current);
    }
    contextSidebarCloseTimerRef.current = setTimeout(() => {
      setContextSidebarHoverOpen(false);
      contextSidebarCloseTimerRef.current = null;
    }, 120);
  };

  const contextSidebarVisible = contextSidebarPinned || contextSidebarHoverOpen;

  const linkClass = ({ isActive }) =>
    `group flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md ${
      isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
    }`;

  const renderMenu = (items) => (
    <div className="space-y-1">
      {items.map((item) => (
        <NavLink key={item.href} to={item.href} className={linkClass}>
          <item.icon className="h-5 w-5 text-gray-400 group-hover:text-gray-500" />
          <span>{item.name}</span>
        </NavLink>
      ))}
    </div>
  );

  const renderSolarMenu = () => (
    <div className="space-y-2">
      {solarSections.map((items, index) => (
        <div key={`solar-section-${items[0]?.href || index}`} className="space-y-2">
          {index > 0 && <div className="mx-3 border-t border-gray-200" />}
          {renderMenu(items)}
        </div>
      ))}
    </div>
  );

  const renderContextTitle = () => {
    if (activeModule === 'billing_core') return 'Central de Cobrancas';
    if (activeModule === 'personal_finance') return 'Financas Pessoais';
    if (activeModule === 'solar') return 'Modulo Solar';
    if (activeModule === 'construtor') return 'Modulo Construtor';
    if (activeModule === 'master_data') return 'Cadastros e Configuracoes';
    if (activeModule === 'radar_lucro') return 'Modulo Radar de Lucro';
    if (activeModule === 'gestao') return 'Gestao';

    const dynamicModule = enabledSoldModules.find((item) => item.key === activeModule);
    return dynamicModule?.nome || 'Modulo Financeiro';
  };

  const renderContextMenu = () => {
    if (activeModule === 'billing_core') return renderMenu(coreItems);
    if (activeModule === 'personal_finance') return renderMenu(personalFinanceItems);
    if (activeModule === 'solar') return renderSolarMenu();
    if (activeModule === 'construtor') return renderMenu(construtorItems);
    if (activeModule === 'master_data') {
      return (
        <div className="space-y-4">
          <div className="space-y-1">
            <div className="px-3 py-2 text-xs uppercase tracking-wide font-semibold text-gray-500">Cadastros Base</div>
            {renderMenu(cadastrosBaseItems)}
          </div>

          {cadastrosConfigItems.length > 0 && (
            <div className="space-y-1">
              <div className="px-3 py-2 text-xs uppercase tracking-wide font-semibold text-gray-500">Configuracoes</div>
              {renderMenu(cadastrosConfigItems)}
            </div>
          )}
        </div>
      );
    }
    if (activeModule === 'radar_lucro') return renderMenu(radarItems);
    if (activeModule === 'gestao') return renderMenu(gestaoItems);
    if (activeModule !== 'financeiro') {
      const dynamicModule = enabledSoldModules.find((item) => item.key === activeModule);
      if (dynamicModule?.default_route) {
        return renderMenu([
          {
            name: dynamicModule.nome,
            href: dynamicModule.default_route,
            icon: MODULE_ICON_MAP[dynamicModule.key] || FALLBACK_MODULE_ICON,
          },
        ]);
      }
    }

    return (
      <div className="space-y-4">
        {renderMenu(financeiroItems)}

        <div className="space-y-1">
          <button
            type="button"
            onClick={() => setFinanceiroRelatoriosOpen((prev) => !prev)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs uppercase tracking-wide font-semibold text-gray-500 hover:text-gray-700"
          >
            <span>Relatorios</span>
            <Bars3Icon className="h-4 w-4" />
          </button>
          {financeiroRelatoriosOpen && <div className="space-y-1">{renderMenu(relatoriosItems)}</div>}
        </div>

        {configuracoesItems.length > 0 && (
          <div className="space-y-1">
            <div className="px-3 py-2 text-xs uppercase tracking-wide font-semibold text-gray-500">Configuracoes</div>
            {renderMenu(configuracoesItems)}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <aside
        className="hidden md:flex fixed inset-y-0 left-0 w-20 border-r border-gray-200 bg-white z-30"
        onMouseEnter={openContextSidebarPreview}
        onMouseLeave={closeContextSidebarPreview}
      >
        <div className="flex flex-col w-full items-center py-4">
          <div className="h-10 w-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-6">
            <CurrencyDollarIcon className="h-6 w-6" />
          </div>

          <nav className="flex-1 flex flex-col gap-2 w-full px-2">
            {moduleButtons
              .filter((item) => item.enabled)
              .map((item) => (
                <button
                  key={item.id}
                  type="button"
                  title={item.label}
                  onClick={() => navigate(item.href)}
                  className={`w-full h-11 rounded-lg flex items-center justify-center transition-colors ${
                    activeModule === item.id ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <item.icon className="h-6 w-6" />
                </button>
              ))}
          </nav>

          <button
            type="button"
            className={`w-11 h-11 rounded-lg flex items-center justify-center mb-2 transition-colors ${
              contextSidebarPinned ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700' : 'bg-primary-100 text-primary-700'
            }`}
            onClick={onToggleContextSidebar}
            title={contextSidebarPinned ? 'Ocultar barra lateral' : 'Fixar barra lateral'}
          >
            {contextSidebarPinned ? <ChevronDoubleLeftIcon className="h-6 w-6" /> : <ChevronDoubleRightIcon className="h-6 w-6" />}
          </button>

          <button
            type="button"
            className="w-11 h-11 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            onClick={handleLogout}
            title="Sair"
          >
            <ArrowRightOnRectangleIcon className="h-6 w-6" />
          </button>
        </div>
      </aside>

      {contextSidebarVisible && (
        <aside
          className={`hidden md:flex fixed inset-y-0 left-20 w-64 border-r border-gray-200 bg-white ${
            contextSidebarPinned ? 'z-20' : 'z-40 shadow-xl'
          }`}
          onMouseEnter={openContextSidebarPreview}
          onMouseLeave={closeContextSidebarPreview}
        >
          <div className="flex flex-col w-full">
            <div className="px-4 pt-4 pb-3 border-b border-gray-200">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Ecossistema</p>
                  <h1 className="text-lg font-semibold text-gray-900">{renderContextTitle()}</h1>
                </div>
                {contextSidebarPinned && (
                  <button
                    type="button"
                    className="p-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    onClick={onToggleContextSidebar}
                    title="Ocultar barra lateral"
                  >
                    <ChevronDoubleLeftIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            <div className="px-4 py-4 border-b border-gray-200">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Empresa Ativa</label>
              <div className="mt-2">
                <div className="flex items-center text-sm text-gray-700 mb-2">
                  <BuildingOffice2Icon className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="truncate">{empresaNome}</span>
                </div>
                <select value={empresaId} onChange={(e) => handleEmpresaChange(e.target.value)} className="form-select text-sm">
                  {empresas.map((empresa) => (
                    <option key={empresa.empresa_id} value={empresa.empresa_id}>
                      {empresa.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-4">{renderContextMenu()}</div>

            <div className="p-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 truncate">{session?.user?.nome || 'Usuario'}</p>
              <p className="text-xs text-gray-500 truncate mb-3">{session?.user?.email || '-'}</p>
              <button className="btn-secondary w-full" onClick={handleLogout}>
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                Sair
              </button>
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

export default Sidebar;
